import { useMutation } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { Subscription, updateNotifications } from 'api/notifications';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { notificationQueryKeys } from 'queries/notification-query-keys';
import { queryClient } from 'queries/queryClient';
import { Notification } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface WithModel {
    modelType: string;
    modelId: number;
}
interface WithoutModel {
    modelType?: never;
    modelId?: never;
}
type ModelProps = WithModel | WithoutModel;
type NotificationToggleFormProps = ModelProps & {
    notification: Notification;
};

const NotificationToggleForm = ({
    modelType,
    modelId,
    notification,
}: NotificationToggleFormProps) => {
    const { accountId } = useCurrentAccount();
    const { error, success } = useToast();

    const defaultValues = {
        subscribed:
            modelType && modelId
                ? isSubscribedToCampaign(notification, modelId)
                : notification.account.subscribed,
        type: notification.type,
        model_type: modelType || null,
        model_id: modelId || null,
    };
    const methods = Form.useGivebutterForm<Subscription>({ defaultValues });
    const { control, handleSubmit } = methods;

    const { mutate: submit } = useMutation<Notification[], GivebutterAxiosError, Subscription>({
        mutationFn: values => updateNotifications(accountId, { subscriptions: [{ ...values }] }),
        onSuccess: response => {
            queryClient.setQueryData(notificationQueryKeys.all(accountId), response);
            success('Your subscription status changed successfully.');
        },
        onError: e => {
            error(e.response?.data.message || 'Something went wrong. Please try again.');
        },
    });

    return (
        <Form.Provider {...methods}>
            <form
                onSubmit={handleSubmit(values => submit(values))}
                className="flex justify-between gap-4 border-b border-b-gray-200 px-8 py-4 last:border-0"
            >
                <div className="flex flex-col">
                    <p className="text-base font-medium">{notification.title}</p>
                    <p className="text-sm text-gray-500">{notification.description}</p>
                </div>
                <Form.Switch
                    name="subscribed"
                    control={control}
                    onCheckedChange={() => handleSubmit(values => submit(values))()}
                />
            </form>
        </Form.Provider>
    );
};

const isSubscribedToCampaign = (notification: Notification, campaignId: number): boolean => {
    const { campaigns, account } = notification;
    if (!campaigns.map(campaign => campaign.id).includes(campaignId)) return account.subscribed;
    return account.subscribed && Boolean(campaigns.find(c => c.id === campaignId)?.subscribed);
};

export { isSubscribedToCampaign, NotificationToggleForm };
